import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const BrainIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-6 h-6">
    <path d="M9.5 2a2.5 2.5 0 0 1 2.45 2H15a2 2 0 0 1 2 2v1.5a2.5 2.5 0 1 1 0 5V14a2 2 0 0 1-2 2h-1v1a3 3 0 0 1-6 0v-1H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h3.05A2.5 2.5 0 0 1 9.5 2z" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const sections = [
  {
    id: 'information-we-collect',
    title: '1. Information We Collect',
    content: [
      {
        subtitle: '1.1 Information You Provide',
        text: 'When you create an account or use MindPlan AI, you may provide us with: account information such as your name and email address; profile information including your preferences and planning goals; content you create within the app including mind maps, task lists, goals, and plans; and communications when you contact our support team.',
      },
      {
        subtitle: '1.2 Information Collected Automatically',
        text: 'When you use MindPlan AI, we automatically collect certain technical information, including: device information (device type, operating system version, unique device identifiers); usage data (features used, screens viewed, time spent in app); performance data (crash reports, error logs, loading times); and log data (IP address, access times, app events).',
      },
      {
        subtitle: '1.3 Information from Third Parties',
        text: 'If you sign in using a third-party service (such as Google or Apple), we receive basic profile information from that provider as permitted by your settings with that provider. We only receive the information necessary to create and manage your account.',
      },
    ],
  },
  {
    id: 'how-we-use',
    title: '2. How We Use Your Information',
    content: [
      {
        subtitle: 'Service Delivery',
        text: 'We use your information to provide, maintain, and improve MindPlan AI. This includes generating AI-powered plans, syncing your data across devices, personalizing your experience, and sending you important service notifications.',
      },
      {
        subtitle: 'AI Processing',
        text: 'The content you enter (goals, ideas, tasks) is processed by our AI systems to generate plans and suggestions. This processing may occur on-device or on our secure servers. We do not use your personal plan content to train our general AI models without your explicit consent.',
      },
      {
        subtitle: 'Analytics and Improvement',
        text: 'We use aggregated, anonymized usage data to understand how users interact with our app, identify bugs, and improve features. This data cannot be used to identify you personally.',
      },
      {
        subtitle: 'Legal Compliance',
        text: 'We may process your information when necessary to comply with applicable laws, respond to legal requests, or protect the rights, property, and safety of our users and the public.',
      },
    ],
  },
  {
    id: 'sharing',
    title: '3. Information Sharing',
    content: [
      {
        subtitle: 'We Do Not Sell Your Data',
        text: 'MindPlan AI does not sell, rent, or trade your personal information to third parties for their marketing purposes.',
      },
      {
        subtitle: 'Service Providers',
        text: 'We share information with trusted third-party service providers who assist us in operating the app, including cloud storage providers (Google Cloud), analytics services (Firebase Analytics), crash reporting (Firebase Crashlytics), and AI processing infrastructure. All providers are bound by strict data processing agreements.',
      },
      {
        subtitle: 'Legal Requirements',
        text: 'We may disclose your information if required by law, court order, or other governmental authority, or when we believe disclosure is necessary to protect our rights, your safety, or the safety of others.',
      },
      {
        subtitle: 'Business Transfers',
        text: 'If MindPlan AI is involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you via email or prominent in-app notice before your information becomes subject to a different privacy policy.',
      },
    ],
  },
  {
    id: 'data-security',
    title: '4. Data Security',
    content: [
      {
        subtitle: 'Encryption',
        text: 'All data transmitted between your device and our servers is encrypted using industry-standard TLS 1.3. Sensitive data stored on our servers is encrypted at rest using AES-256 encryption.',
      },
      {
        subtitle: 'Access Controls',
        text: 'We implement strict access controls to ensure that only authorized personnel can access user data, and only when necessary for specific business purposes. All access is logged and audited.',
      },
      {
        subtitle: 'Security Incidents',
        text: 'In the unlikely event of a data breach that affects your personal information, we will notify you in accordance with applicable law and take immediate steps to mitigate any harm.',
      },
    ],
  },
  {
    id: 'your-rights',
    title: '5. Your Rights and Choices',
    content: [
      {
        subtitle: 'Access and Portability',
        text: 'You have the right to access the personal information we hold about you and to receive a copy of it in a structured, machine-readable format. You can export your data from within the app at any time via Settings > Account > Export Data.',
      },
      {
        subtitle: 'Correction',
        text: 'You can update or correct inaccurate information through the app settings. If you need assistance correcting information that you cannot update yourself, please contact us at support@mindplanai.com.',
      },
      {
        subtitle: 'Deletion',
        text: 'You have the right to request deletion of your account and all associated data. You can do this from within the app (Settings > Account > Delete Account) or by visiting our Data Deletion page. We will process deletion requests within 30 days.',
      },
      {
        subtitle: 'Opt-Out',
        text: 'You can opt out of non-essential communications at any time via your notification settings in the app. You can also opt out of analytics data collection by adjusting your privacy settings within the app.',
      },
    ],
  },
  {
    id: 'third-party',
    title: '6. Third-Party Services',
    content: [
      {
        subtitle: 'Google Play Services',
        text: 'Our Android app uses Google Play Services for account authentication, analytics, and in-app purchases. Google\'s privacy policy applies to data processed by their services.',
      },
      {
        subtitle: 'Firebase',
        text: 'We use Google Firebase for real-time database, authentication, analytics, and crash reporting. Firebase processes data in accordance with Google\'s data processing terms.',
      },
      {
        subtitle: 'OpenAI / AI Providers',
        text: 'Your plan content may be processed by AI providers to generate plans and suggestions. This processing is subject to strict data minimization and our data processing agreements ensure your content is not used to train third-party models.',
      },
    ],
  },
  {
    id: 'children',
    title: '7. Children\'s Privacy',
    content: [
      {
        subtitle: 'Age Requirement',
        text: 'MindPlan AI is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If we discover that we have inadvertently collected information from a child under 13, we will delete it immediately.',
      },
      {
        subtitle: 'Parental Control',
        text: 'If you believe your child has provided us with personal information without your consent, please contact us at support@mindplanai.com and we will take prompt action.',
      },
    ],
  },
  {
    id: 'changes',
    title: '8. Changes to This Policy',
    content: [
      {
        subtitle: 'Notification of Changes',
        text: 'We may update this Privacy Policy from time to time. When we make significant changes, we will notify you through the app, via email, or by posting a prominent notice on our website. The updated policy will include the date it was last revised.',
      },
      {
        subtitle: 'Continued Use',
        text: 'Your continued use of MindPlan AI after the effective date of a revised Privacy Policy constitutes your acceptance of the new policy. If you do not agree to the updated terms, please discontinue use and request account deletion.',
      },
    ],
  },
  {
    id: 'contact',
    title: '9. Contact Us',
    content: [
      {
        subtitle: 'Privacy Inquiries',
        text: 'If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact our Privacy team:\n\nEmail: privacy@mindplanai.com\nSupport: support@mindplanai.com\n\nMindPlan AI\nData Privacy Department\n\nWe aim to respond to all privacy inquiries within 72 hours.',
      },
    ],
  },
];

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Privacy Policy – MindPlan AI';
  }, []);

  return (
    <div className="bg-neutral-950 text-white min-h-screen">
      {/* Header */}
      <div className="relative bg-hero-gradient border-b border-white/5 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-lavender-700/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-lavender-500/10 rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 relative z-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-white/40 text-sm mb-8">
            <Link to="/" className="hover:text-lavender-300 transition-colors">Home</Link>
            <ChevronRightIcon />
            <span className="text-lavender-300">Privacy Policy</span>
          </div>

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 mb-8 group w-fit">
            <div className="w-10 h-10 rounded-xl bg-lavender-gradient flex items-center justify-center shadow-lavender">
              <BrainIcon />
            </div>
            <span className="font-display font-bold text-xl">MindPlan AI</span>
          </Link>

          <div className="inline-flex items-center gap-2 bg-lavender-600/20 border border-lavender-500/30 rounded-full px-4 py-1.5 mb-4">
            <span className="text-lavender-300 text-xs font-medium tracking-wider uppercase">Legal Document</span>
          </div>

          <h1 className="font-display text-4xl md:text-5xl font-bold mb-4">
            Privacy <span className="gradient-text">Policy</span>
          </h1>
          <p className="text-white/50 text-lg leading-relaxed max-w-2xl">
            Your privacy is fundamental to everything we do at MindPlan AI. This policy explains how we collect, use, and protect your personal information.
          </p>

          <div className="flex flex-wrap gap-4 mt-8">
            <div className="glass-card px-4 py-2.5 text-sm text-white/60">
              <span className="text-white/40">Last Updated:</span>{' '}
              <span className="text-lavender-300 font-medium">July 25, 2025</span>
            </div>
            <div className="glass-card px-4 py-2.5 text-sm text-white/60">
              <span className="text-white/40">Effective:</span>{' '}
              <span className="text-lavender-300 font-medium">July 25, 2025</span>
            </div>
            <div className="glass-card px-4 py-2.5 text-sm text-white/60">
              <span className="text-white/40">Version:</span>{' '}
              <span className="text-lavender-300 font-medium">2.0</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-4 gap-12">
          {/* Table of Contents – sticky sidebar */}
          <aside className="hidden lg:block lg:col-span-1">
            <div className="sticky top-24 glass-card p-5">
              <h3 className="font-semibold text-white text-sm mb-4 tracking-wide">Contents</h3>
              <nav className="flex flex-col gap-2">
                {sections.map(section => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="text-white/40 text-xs hover:text-lavender-300 transition-colors duration-200 leading-relaxed py-0.5 border-l-2 border-transparent hover:border-lavender-500 pl-2"
                  >
                    {section.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main content */}
          <main className="lg:col-span-3">
            {/* Intro */}
            <div className="glass-card-light p-6 mb-10 rounded-2xl">
              <p className="text-white/70 leading-relaxed text-[15px]">
                This Privacy Policy describes how <strong className="text-lavender-300">MindPlan AI</strong> ("we," "our," or "us") collects, uses, shares, and protects information about you when you use our mobile application and related services (collectively, the "Service"). By using MindPlan AI, you agree to the collection and use of information in accordance with this policy.
              </p>
            </div>

            {/* Sections */}
            <div className="flex flex-col gap-12">
              {sections.map(section => (
                <section key={section.id} id={section.id} className="scroll-mt-28">
                  <h2 className="font-display text-2xl font-bold text-white mb-6 pb-3 border-b border-white/10">
                    <span className="gradient-text">{section.title}</span>
                  </h2>
                  <div className="flex flex-col gap-6">
                    {section.content.map((item, idx) => (
                      <div key={idx} className="flex flex-col gap-2">
                        <h3 className="font-semibold text-lavender-300 text-[15px]">{item.subtitle}</h3>
                        <p className="text-white/60 leading-relaxed text-[15px] whitespace-pre-line">{item.text}</p>
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            {/* Footer links */}
            <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
              <p className="text-white/30 text-sm">
                &copy; {new Date().getFullYear()} MindPlan AI. All rights reserved.
              </p>
              <div className="flex gap-4">
                <Link to="/data-deletion" className="text-lavender-400 text-sm hover:text-lavender-300 transition-colors">
                  Data Deletion Request
                </Link>
                <Link to="/" className="text-white/40 text-sm hover:text-lavender-300 transition-colors">
                  Back to Home
                </Link>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
